import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, MessageCircle, Linkedin, Sparkles, Download } from "lucide-react";
import { api } from "../lib/api";
import { DIMENSIONS, LEVELS, levelForScore, questionText, GUIDE } from "../data/lightAssessment";

const errMsg = (e, fallback) =>
  e?.response?.data?.detail && typeof e.response.data.detail === "string"
    ? e.response.data.detail
    : fallback;

// Maturity radar — exact replica of the full assessment's "horizon dial" geometry.
const SIZE = 420, CX = 210, CY = 216, RMAX = 150, N = 8;
const ring = (l) => RMAX * (l / 5);
const ang = (i) => -Math.PI / 2 + i * ((2 * Math.PI) / N);
const ptAt = (i, l) => [CX + Math.cos(ang(i)) * ring(l), CY + Math.sin(ang(i)) * ring(l)];

// Level-ring legend swatches (match .r-dot.r1..r5 in the full assessment).
const ringStyles = [
  { borderColor: "rgba(3,33,126,0.10)" },
  { borderColor: "rgba(3,33,126,0.18)" },
  { borderColor: "rgba(3,33,126,0.40)", borderStyle: "dashed" },
  { borderColor: "rgba(3,33,126,0.55)" },
  { background: "rgba(1,55,218,0.12)", borderColor: "#0137DA" },
];

const LightRadar = ({ dims }) => {
  const poly = dims.map((d, i) => ptAt(i, d.score).map((n) => n.toFixed(1)).join(",")).join(" ");
  return (
    <svg className="evc-lr-radar" viewBox={`0 0 ${SIZE} ${SIZE}`} preserveAspectRatio="xMidYMid meet"
      role="img" aria-label="Maturity radar" data-testid="light-radar-chart">
      {[1, 2, 3, 4, 5].map((l) => (
        <g key={l}>
          <circle className={`horizon-ring${l === 3 ? " active" : ""}`} cx={CX} cy={CY} r={ring(l)} />
          <text className="horizon-label" x={CX + 4} y={CY - ring(l) + 3}>L{l}</text>
        </g>
      ))}
      {dims.map((d, i) => {
        const [x, y] = ptAt(i, 5);
        return <line key={d.code} className="horizon-spoke" x1={CX} y1={CY} x2={x} y2={y} />;
      })}
      <polygon className="horizon-arc" points={poly} />
      {dims.map((d, i) => {
        const [x, y] = ptAt(i, d.score);
        return (
          <g key={i}>
            <circle className="horizon-dot" cx={x} cy={y} r="6" />
            <text className="horizon-dot-num" x={x} y={y - 11}>{d.score.toFixed(1)}</text>
          </g>
        );
      })}
      {dims.map((d, i) => {
        const c = Math.cos(ang(i));
        const lx = CX + c * (RMAX + 28);
        const ly = CY + Math.sin(ang(i)) * (RMAX + 28);
        const anchor = Math.abs(c) < 0.3 ? "middle" : c > 0 ? "start" : "end";
        return (
          <text key={d.code} className="horizon-dim-label" x={lx} y={ly + 4} textAnchor={anchor}>{d.code}</text>
        );
      })}
    </svg>
  );
};

export const LightResults = ({ persona, answers }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const computed = useMemo(() => {
    const dims = DIMENSIONS.map((d) => {
      const score = answers[d.code] || 0;
      return { code: d.code, label: d.label, sub: d.sub, score, level: levelForScore(score).code };
    });
    const scores = dims.map((d) => d.score).filter(Boolean);
    const overall = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    const lvl = levelForScore(overall);
    return { dims, overall, lvl };
  }, [answers]);

  const { dims, overall, lvl } = computed;
  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.company.trim() && /\S+@\S+\.\S+/.test(form.email);

  const waLink = useMemo(() => {
    const msg = `Hi EVC team, I just completed the light ITFM snapshot (${lvl.code} ${lvl.name} - ${lvl.archetype}). I'd like to go deeper with the full assessment and its actionable insights.`;
    return `https://wa.me/${GUIDE.whatsappNumber}?text=${encodeURIComponent(msg)}`;
  }, [lvl]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid) {
      setError("Please add your name, company and a valid work email.");
      return;
    }
    setError("");
    setLoading(true);
    const results = {
      overall: Number(overall.toFixed(2)),
      levelCode: lvl.code,
      levelName: lvl.name,
      archetype: lvl.archetype,
      persona: persona || null,
      internalRef: "",
      counts: { answered: dims.length, skipped: 0, unanswered: 0, total: dims.length },
      dimensions: dims.map((d) => ({ code: d.code, label: d.label, score: d.score })),
      questions: DIMENSIONS.map((d) => {
        const score = answers[d.code] || 0;
        const opt = score ? d.options[score - 1] : null;
        return {
          dimCode: d.code,
          dimension: d.label,
          q: 1,
          question: questionText(d, persona),
          level: opt ? opt.level : "",
          score: score || "",
          answer: opt ? opt.text : "",
          remark: "",
        };
      }),
      capturedAt: new Date().toISOString(),
      source: "light",
    };
    try {
      await api.post("/light/submit", {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        results,
      });
      setDone(true);
      toast.success("Result saved", { description: "Your contact is ready below." });
    } catch (err) {
      const m = errMsg(err, "Could not save. Please try again.");
      setError(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="evc-lite-results" data-testid="light-results">
      {/* 1 — Archetype masthead */}
      <motion.div
        className="evc-lr-masthead"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="light-masthead"
      >
        <div>
          <div className="evc-lr-eyebrow">
            <span className="evc-lr-chip">Findings</span>
            <span className="evc-lr-chip">Indicative · 8-question snapshot</span>
          </div>
          <span className="evc-lr-title-above">Your operating archetype</span>
          <h1 className="evc-lr-title" data-testid="light-archetype">{lvl.archetype}.</h1>
          <p className="evc-lr-sub">{lvl.desc}</p>
          <div className="evc-lr-level-line">
            <span>Maturity</span> <b>{lvl.code}</b> <span>{lvl.name}</span>
          </div>
        </div>
        <div className="evc-lr-score">
          <div className="evc-lr-score-label">Overall score</div>
          <div className="evc-lr-score-num" data-testid="light-overall">
            {overall.toFixed(1)}<span className="of">/5</span>
          </div>
          <div className="evc-lr-score-denom">Average across {dims.length} answered questions</div>
        </div>
      </motion.div>

      {/* 2 — Level ladder */}
      <div className="evc-lr-strip" data-testid="light-level-strip">
        {LEVELS.map((l, i) => (
          <div key={l.code} className={`evc-lr-cell${l.idx === lvl.idx ? " active" : ""}`} data-testid={`light-level-${l.code}`}>
            <span className={`arch-tip cell-tip${i >= 3 ? " last" : ""}`} tabIndex={0} data-testid={`light-level-tip-${l.code}`}>
              <div className="lv-num">Level {String(l.idx + 1).padStart(2, "0")}</div>
              <div className="lv-name">{l.name}</div>
              <div className="lv-arch">{l.archetype}</div>
              <span className="arch-tip-panel" role="tooltip">
                <span className="arch-tip-eyebrow">Level {String(l.idx + 1).padStart(2, "0")} · {l.name}</span>
                <span className="arch-tip-name">{l.archetype}</span>
                <p className="arch-tip-body">{l.desc}</p>
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* 3 — Maturity radar + dimension breakdown */}
      <div className="evc-lr-card" data-testid="light-radar-breakdown">
        <div className="evc-lr-card-head">
          <span className="eyebrow">Maturity radar · Dimension breakdown</span>
          <span className="fig">Fig. 01</span>
        </div>
        <div className="evc-lr-merged">
          <div className="evc-lr-merged-left">
            <div className="evc-lr-radar-wrap">
              <LightRadar dims={dims} />
            </div>
            <div className="evc-lr-legend">
              <div className="evc-lr-legend-section">
                <div className="evc-lr-legend-title">Levels (rings)</div>
                <div className="evc-lr-rings">
                  {LEVELS.map((l) => (
                    <span className="evc-lr-ring" key={l.code}>
                      <i style={ringStyles[l.idx]} />{l.code} {l.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="evc-lr-legend-section">
                <div className="evc-lr-legend-title">Dimensions (axes)</div>
                <div className="evc-lr-dims-legend">
                  {dims.map((d) => (
                    <span className="evc-lr-dim-leg" key={d.code}><b>{d.code}</b> {d.label}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="evc-lr-rows">
            {dims.map((d, i) => (
              <div className="evc-lr-row" key={d.code} data-testid={`light-bar-${d.code}`}>
                <div className="d-num">{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className="d-name">{d.label}</div>
                  <div className="d-sub">{d.sub}</div>
                </div>
                <div className="evc-lr-bar-cell">
                  <div className="evc-lr-track">
                    <motion.div className="evc-lr-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${(d.score / 5) * 100}%` }}
                      transition={{ duration: 0.9, delay: 0.15 + i * 0.05, ease: [0.2, 0.6, 0.2, 1] }} />
                    <div className="evc-lr-ticks"><span /><span /><span /><span /><span /></div>
                  </div>
                </div>
                <div className="d-val">{d.score.toFixed(1)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lead capture / get in contact */}
      {!done ? (
        <form className="evc-lite-leadform" onSubmit={submit} noValidate data-testid="light-lead-form">
          <span className="evc-card-kicker"><Sparkles size={13} strokeWidth={2.2} /> Like what you see?</span>
          <h3 className="evc-lite-leadform-title">Get your result and your next steps</h3>
          <p className="evc-lite-leadform-sub">
            This is the snapshot. For the full scope - the complete assessment with deeper, real,
            actionable insights across all 56 questions and eight dimensions - save your result below,
            then get in contact with us.
          </p>
          <div className="evc-lite-leadgrid">
            <input className="evc-input" placeholder="Full name" value={form.name} onChange={onField("name")} data-testid="light-input-name" />
            <input className="evc-input" placeholder="Company" value={form.company} onChange={onField("company")} data-testid="light-input-company" />
          </div>
          <input className="evc-input" type="email" placeholder="Work email" value={form.email} onChange={onField("email")} data-testid="light-input-email" />
          {error && <div className="evc-error" data-testid="light-form-error">{error}</div>}
          <button type="submit" className="evc-btn" disabled={loading} data-testid="light-submit-btn">
            {loading ? <Loader2 size={17} className="evc-spin" /> : null}
            {loading ? "Saving..." : "Save my result"}
            {!loading && <ArrowRight size={17} />}
          </button>
        </form>
      ) : (
        <motion.div
          className="evc-lite-contact"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="light-contact-block"
        >
          <div className="evc-lr-download-bar" data-testid="light-download-bar">
            <span>Your snapshot is saved. Take a copy with you:</span>
            <button className="evc-btn evc-btn-dark" onClick={() => window.print()} data-testid="light-download-btn">
              <Download size={16} /> Download / print result
            </button>
          </div>
          <div className="evc-lite-guide">
            <img className="evc-lite-guide-photo" src={GUIDE.photo} alt={GUIDE.name} />
            <div>
              <div className="evc-lite-guide-eyebrow">Your guide</div>
              <div className="evc-lite-guide-name">{GUIDE.name}</div>
              <div className="evc-lite-guide-role">{GUIDE.role}</div>
            </div>
          </div>

          <div className="evc-lite-cta-box" data-testid="light-request-full">
            <h3 className="evc-lite-cta-title"><Sparkles size={16} /> Go deeper with the full assessment</h3>
            <p className="evc-lite-cta-sub">
              You've seen the snapshot. For the full scope - the complete diagnostic with deeper, real,
              and genuinely actionable insights across all 56 questions and eight dimensions - get in
              contact and we'll guide you through it.
            </p>
            <div className="evc-lite-cta-actions">
              <a className="evc-lite-wa" href={waLink} target="_blank" rel="noopener noreferrer" data-testid="light-request-code-btn">
                <MessageCircle size={16} /> Get in contact on WhatsApp
              </a>
              <a className="evc-lite-li" href={GUIDE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="light-linkedin-btn">
                <Linkedin size={16} /> Connect on LinkedIn
              </a>
            </div>
            <p className="evc-lite-cta-note">
              * Getting in contact this way keeps an EVC expert in the loop, so you get tailored
              guidance on your next steps - not just a login.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};
